/**
 * Direct SMTP client — transport-agnostic, mirrors iflow-direct's structure.
 *
 * Implements RFC 5321 (SMTP), RFC 3207 (STARTTLS), RFC 4954 (AUTH),
 * RFC 4616 (PLAIN), RFC 4752 (XOAUTH2 — Google's variant).
 *
 * No Node.js dependencies. Caller injects an ImapTransport (the iflow-direct
 * transport interface — same TCP byte-stream abstraction works for SMTP):
 *   - Desktop: NodeTransport from @bobfrankston/iflow-node
 *   - Browser/WebView: BridgeTransport from @bobfrankston/iflow-direct
 *
 * Only one command at a time per connection (SMTP is synchronous).
 */

import type { TcpTransport, TransportFactory } from "@bobfrankston/tcp-transport";
import type {
    SmtpAuth, SmtpClientConfig, SmtpEnvelope,
    SmtpResponse, SmtpSendResult, SmtpRejectedRecipient,
} from "./types.js";

export class SmtpClient {
    private transport: TcpTransport;
    private config: Required<Omit<SmtpClientConfig, "auth" | "secure">> & Pick<SmtpClientConfig, "auth" | "secure">;
    private buffer = "";
    private capabilities = new Set<string>();
    private _connected = false;
    private pending: {
        resolve: (resp: SmtpResponse) => void;
        reject: (err: Error) => void;
        timer: ReturnType<typeof setTimeout> | null;
    } | null = null;

    constructor(config: SmtpClientConfig, transportFactory: TransportFactory) {
        this.config = {
            host: config.host,
            port: config.port,
            secure: config.secure,
            auth: config.auth,
            localname: config.localname || "localhost",
            timeoutMs: config.timeoutMs ?? 30000,
            verbose: config.verbose || false,
        };
        this.transport = transportFactory();
    }

    get connected(): boolean { return this._connected; }
    get extensions(): Set<string> { return new Set(this.capabilities); }

    /** Connect, EHLO, STARTTLS if needed, AUTH if configured. */
    async connect(): Promise<void> {
        const useTls = this.config.secure ?? (this.config.port === 465);

        this.transport.onData((data) => this.handleData(data));
        this.transport.onClose(() => {
            this._connected = false;
            this.failPending(new Error("SMTP connection closed"));
        });
        this.transport.onError((err) => {
            if (this.config.verbose) console.error(`  [smtp] transport error: ${err.message}`);
            this.failPending(err);
        });

        await this.transport.connect(this.config.host, this.config.port, useTls, this.config.host);
        this._connected = true;

        // Server greeting (220 ...)
        const greeting = await this.readResponse();
        if (greeting.code !== 220) throw new Error(`SMTP greeting failed: ${greeting.code} ${greeting.text}`);

        await this.ehlo();

        // STARTTLS upgrade if not already TLS and server supports it.
        if (!useTls && this.capabilities.has("STARTTLS")) {
            const r = await this.command("STARTTLS");
            if (r.code !== 220) throw new Error(`STARTTLS failed: ${r.code} ${r.text}`);
            await this.transport.upgradeTLS(this.config.host);
            // Re-issue EHLO after TLS — capabilities can change post-handshake (notably AUTH appears).
            await this.ehlo();
        }

        if (this.config.auth) {
            await this.authenticate(this.config.auth);
        }
    }

    /** Send one message. Envelope determines MAIL FROM / RCPT TO; raw is the full RFC 2822 message. */
    async sendMail(envelope: SmtpEnvelope, raw: string | Uint8Array): Promise<SmtpSendResult> {
        if (!this._connected) throw new Error("SMTP: not connected");
        if (!envelope.from) throw new Error("SMTP: missing sender");
        if (!envelope.to?.length) throw new Error("SMTP: no recipients");

        // MAIL FROM
        const sizeHint = typeof raw === "string"
            ? new TextEncoder().encode(raw).byteLength
            : raw.byteLength;
        const sizeExt = this.capabilities.has("SIZE") ? ` SIZE=${sizeHint}` : "";
        const mf = await this.command(`MAIL FROM:<${envelope.from}>${sizeExt}`);
        if (mf.code !== 250) throw new Error(`MAIL FROM rejected: ${mf.code} ${mf.text}`);

        // RCPT TO — collect per-recipient errors instead of failing whole send.
        const accepted: string[] = [];
        const rejected: SmtpRejectedRecipient[] = [];
        for (const addr of envelope.to) {
            const r = await this.command(`RCPT TO:<${addr}>`);
            if (r.code === 250 || r.code === 251) {
                accepted.push(addr);
            } else {
                rejected.push({ address: addr, code: r.code, message: r.text });
                if (this.config.verbose) console.error(`  [smtp] RCPT rejected ${addr}: ${r.code} ${r.text}`);
            }
        }
        if (accepted.length === 0) {
            // No recipients accepted — RSET so the connection can be reused for next send.
            try { await this.command("RSET"); } catch { /* ignore */ }
            throw new Error(`SMTP: all recipients rejected: ${rejected.map(r => `${r.address} (${r.code})`).join(", ")}`);
        }

        // DATA — server says 354, then we stream the body and finish with \r\n.\r\n
        const data = await this.command("DATA");
        if (data.code !== 354) throw new Error(`DATA rejected: ${data.code} ${data.text}`);

        const body = typeof raw === "string" ? raw : new TextDecoder().decode(raw);
        const stuffed = dotStuff(body);
        // Ensure body ends with CRLF before terminator
        const term = stuffed.endsWith("\r\n") ? ".\r\n" : "\r\n.\r\n";
        // Send body + terminator, then wait for the final 250
        await this.transport.write(stuffed + term);
        const resp = await this.readResponse();
        if (resp.code !== 250) throw new Error(`Message rejected after DATA: ${resp.code} ${resp.text}`);

        return { accepted, rejected, response: resp.text };
    }

    /** Polite shutdown. */
    async quit(): Promise<void> {
        if (!this._connected) return;
        try {
            // Best-effort; some servers close before sending 221.
            await Promise.race([
                this.command("QUIT"),
                new Promise<void>((resolve) => setTimeout(resolve, 2000)) as Promise<any>,
            ]);
        } catch { /* ignore */ }
        try { this.transport.close(); } catch { /* ignore */ }
        this._connected = false;
    }

    /** Hard close — for tests / aborts. */
    close(): void {
        try { this.transport.close(); } catch { /* ignore */ }
        this._connected = false;
        this.failPending(new Error("SMTP closed"));
    }

    // ── Internals ──

    private async ehlo(): Promise<void> {
        const r = await this.command(`EHLO ${this.config.localname}`);
        if (r.code !== 250) {
            // Fallback to HELO for ancient servers without ESMTP.
            const helo = await this.command(`HELO ${this.config.localname}`);
            if (helo.code !== 250) throw new Error(`EHLO/HELO failed: ${r.code} ${r.text}`);
            this.capabilities.clear();
            return;
        }
        this.capabilities.clear();
        // EHLO body is keyword [SP params] per line, first line is greeting text.
        const lines = r.text.split("\n").slice(1); // skip greeting line
        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            const sp = trimmed.indexOf(" ");
            const keyword = (sp < 0 ? trimmed : trimmed.substring(0, sp)).toUpperCase();
            this.capabilities.add(keyword);
            // AUTH advertises space-separated mechanisms — record each as `AUTH=<MECH>`.
            if (keyword === "AUTH" && sp > 0) {
                for (const mech of trimmed.substring(sp + 1).split(/\s+/)) {
                    if (mech) this.capabilities.add(`AUTH=${mech.toUpperCase()}`);
                }
            }
        }
    }

    private async authenticate(auth: SmtpAuth): Promise<void> {
        switch (auth.method) {
            case "PLAIN": {
                // RFC 4616: \0username\0password, base64-encoded.
                const token = b64utf8(`\x00${auth.user}\x00${auth.pass}`);
                const r = await this.command(`AUTH PLAIN ${token}`, /*sensitive*/ true);
                if (r.code !== 235) throw new Error(`AUTH PLAIN failed: ${r.code} ${r.text}`);
                return;
            }
            case "LOGIN": {
                // Two-step: server prompts 334 base64("Username:") then 334 base64("Password:").
                const r1 = await this.command("AUTH LOGIN", true);
                if (r1.code !== 334) throw new Error(`AUTH LOGIN start failed: ${r1.code} ${r1.text}`);
                const r2 = await this.command(b64utf8(auth.user), true);
                if (r2.code !== 334) throw new Error(`AUTH LOGIN user rejected: ${r2.code} ${r2.text}`);
                const r3 = await this.command(b64utf8(auth.pass), true);
                if (r3.code !== 235) throw new Error(`AUTH LOGIN failed: ${r3.code} ${r3.text}`);
                return;
            }
            case "XOAUTH2": {
                // Google variant: user=<USER>\x01auth=Bearer <TOKEN>\x01\x01
                const blob = b64utf8(`user=${auth.user}\x01auth=Bearer ${auth.token}\x01\x01`);
                const r = await this.command(`AUTH XOAUTH2 ${blob}`, true);
                if (r.code === 235) return;
                // On failure server may emit 334 with a base64 JSON error and wait for an empty
                // continuation line before sending the final tagged 535. Send "" to flush, then
                // surface the original error text.
                if (r.code === 334) {
                    try { await this.command(""); } catch { /* ignore */ }
                    throw new Error(`AUTH XOAUTH2 failed`);
                }
                throw new Error(`AUTH XOAUTH2 failed: ${r.code} ${r.text}`);
            }
        }
    }

    /** Send one command and await the next response. Sensitive=true redacts the log line. */
    private command(line: string, sensitive = false): Promise<SmtpResponse> {
        if (this.config.verbose) {
            console.log(`  [smtp] > ${sensitive ? "<redacted>" : line}`);
        }
        const writeP = this.transport.write(`${line}\r\n`);
        const respP = this.readResponse();
        return writeP.then(() => respP);
    }

    private readResponse(): Promise<SmtpResponse> {
        return new Promise((resolve, reject) => {
            if (this.pending) {
                reject(new Error("SMTP: previous command still pending"));
                return;
            }
            const timer = setTimeout(() => {
                this.pending = null;
                try { this.transport.close(); } catch { /* ignore */ }
                reject(new Error(`SMTP inactivity timeout (${this.config.timeoutMs}ms)`));
            }, this.config.timeoutMs);
            this.pending = {
                resolve: (resp) => { if (timer) clearTimeout(timer); resolve(resp); },
                reject: (err) => { if (timer) clearTimeout(timer); reject(err); },
                timer,
            };
            // Drain anything that might already be in the buffer (e.g. greeting on connect).
            this.tryParseResponse();
        });
    }

    private handleData(data: Uint8Array): void {
        // SMTP responses are 7-bit ASCII per RFC 5321 — UTF-8 decode is a
        // superset and produces the same characters. Decode each chunk
        // once on receipt; the parser stays string-based since SMTP has no
        // literal-style framing where byte fidelity matters.
        this.buffer += this.utf8Decoder.decode(data);
        this.tryParseResponse();
    }
    private utf8Decoder = new TextDecoder("utf-8");

    /** SMTP multi-line replies: each intermediate line "NNN-TEXT", final "NNN TEXT".
     *  Pulls one complete response off the buffer if available and resolves the
     *  pending command. */
    private tryParseResponse(): void {
        if (!this.pending) return;
        // Find the boundary — a line whose 4th char is " " (space, not "-").
        const lines: string[] = [];
        let consumed = 0;
        let code = 0;
        const buf = this.buffer;
        let pos = 0;
        while (true) {
            const eol = buf.indexOf("\r\n", pos);
            if (eol < 0) return; // wait for more data
            const line = buf.substring(pos, eol);
            lines.push(line);
            pos = eol + 2;
            if (line.length >= 4) {
                const codeStr = line.substring(0, 3);
                const sep = line.charAt(3);
                if (/^\d{3}$/.test(codeStr)) {
                    if (sep === " " || sep === "") {
                        code = parseInt(codeStr);
                        consumed = pos;
                        break;
                    } else if (sep !== "-") {
                        // Malformed — consume what we have and surface as code 0.
                        consumed = pos;
                        break;
                    }
                } else {
                    // Not an SMTP-shaped line — bail and surface as 0.
                    consumed = pos;
                    break;
                }
            }
        }

        const raw = buf.substring(0, consumed);
        this.buffer = buf.substring(consumed);

        const text = lines.map(l => l.length >= 4 ? l.substring(4) : l).join("\n");
        if (this.config.verbose) {
            console.log(`  [smtp] < ${code} ${text.split("\n").join(" / ")}`);
        }

        const p = this.pending;
        this.pending = null;
        p.resolve({ code, text, raw });

        // If more data is buffered, schedule another parse pass — caller may already
        // have queued the next readResponse.
        if (this.buffer.length > 0) {
            queueMicrotask(() => this.tryParseResponse());
        }
    }

    private failPending(err: Error): void {
        if (!this.pending) return;
        const p = this.pending;
        this.pending = null;
        p.reject(err);
    }
}

/** RFC 5321 §4.5.2 dot-stuffing: any line beginning with "." gets a leading "."
 *  prepended so it doesn't terminate DATA prematurely. Operates on CRLF or LF
 *  line endings; output is normalized to CRLF. */
export function dotStuff(body: string): string {
    // Normalize CRLF — leave existing CRLF alone; convert lone LF to CRLF.
    const normalized = body.replace(/\r?\n/g, "\r\n");
    // Insert "." before any line starting with "."
    return normalized.replace(/(^|\r\n)\./g, "$1..");
}

/** UTF-8-safe base64 (avoids the latin-1 limitation of bare btoa). */
function b64utf8(s: string): string {
    if (typeof Buffer !== "undefined" && Buffer.from) {
        return Buffer.from(s, "utf-8").toString("base64");
    }
    // Browser path
    const bytes = new TextEncoder().encode(s);
    let bin = "";
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return btoa(bin);
}
